import type { Comic, ComicSummary } from '@panels/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ComicStrip } from '../components/ComicStrip.js';
import { EmptyNote, ErrorNote, Screen, Spinner } from '../components/ui.js';
import { ApiError, getSharedComic, getSharedRoom } from '../lib/api.js';

/** Public session gallery: every finished comic from one room (§9). */
export function SharedRoom() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [comics, setComics] = useState<ComicSummary[] | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareToken) return;
    getSharedRoom(shareToken)
      .then((result) => {
        setComics(result.comics);
        setCode(result.room.code);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That link is no longer available'));
  }, [shareToken]);

  if (error) {
    return (
      <Screen title="Panels">
        <ErrorNote>{error}</ErrorNote>
        <Link className="button" to="/">
          Go to Panels
        </Link>
      </Screen>
    );
  }
  if (!comics) return <Spinner label="Loading comics…" />;

  return (
    <Screen wide>
      <div className="share-head">
        <span className="home-title small">PANELS</span>
        <h2>Room {code}</h2>
        <p className="muted">
          {comics.length} {comics.length === 1 ? 'comic' : 'comics'}
        </p>
      </div>

      {comics.length === 0 ? <EmptyNote>No finished comics in this room yet.</EmptyNote> : null}

      <ul className="gallery">
        {comics.map((summary) => (
          <li key={summary.sheetId}>
            <SharedComicCard summary={summary} />
          </li>
        ))}
      </ul>

      <p className="muted small center">
        Made with <Link to="/">Panels</Link> — a drawing game for friends.
      </p>
    </Screen>
  );
}

function SharedComicCard({ summary }: { summary: ComicSummary }) {
  const ref = useRef<HTMLDivElement>(null);
  const [comic, setComic] = useState<Comic | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || comic) return;

    if (typeof IntersectionObserver === 'undefined') {
      getSharedComic(summary.shareToken)
        .then(setComic)
        .catch(() => undefined);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        getSharedComic(summary.shareToken)
          .then(setComic)
          .catch(() => undefined);
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [summary.shareToken, comic]);

  return (
    <div ref={ref} className="gallery-item static">
      {comic ? <ComicStrip comic={comic} replay={false} zoomable /> : <div className="thumb-placeholder" aria-hidden="true" />}
      <div className="gallery-meta">
        <strong>{summary.title ?? `${summary.starter.displayName}'s comic`}</strong>
        <span className="muted">
          {summary.status === 'abandoned' ? 'unfinished · ' : ''}
          {summary.participants.map((p) => p.displayName).join(', ')}
        </span>
      </div>
    </div>
  );
}
